import {
  AuthenticationConfigObject,
  DefaultCountryCode,
  DefaultRequestParams,
  StringStringMap,
  SupportedLanguages,
} from './GeneralTypes';
import { UrlState } from './UrlTypes';
import { ABTestCookie } from './enums';

export type AemConfigurationShoplanding = {
  timeout_reset_seconds: number;
};

export type AemConfigurationPageInfo = {
  isBasketOverviewPage: boolean;
  isBrandAsCategoryPage: boolean;
  isBrandPage: boolean;
  isBuyPage: boolean;
  isCategoryPage: boolean;
  isConfirmationPage: boolean;
  isGiftCardPage: boolean;
  isGiftVoucherPage: boolean;
  isListerPage: boolean;
  isHomePage: boolean;
  isPDPPage: boolean;
  isSearchPage: boolean;
  isWishlistPage: boolean;
  isNotFoundPage: boolean;
  pdpTemplateName: string;
  strippedUrl: string;
};

export type AemConfigurationAlternateUrl = {
  en?: string;
  fr?: string;
  nl?: string;
  de?: string;
};

export type FredHopper = {
  suggest: string;
  search: string;
  scanner_search: string;
  compare: string;
};

export type AemConfigurationServices = {
  api: string;
  fredHopper: FredHopper;
  graphql: string;
};

export type AemConfigurationDeliveryPromises = {
  mostImportantDeliveryOption: Array<string>;
};

export type AemConfigurationThirdpartyZopim = {
  enable: string;
  windowSize: string;
  showBadge: string;
  badgeColor: string;
  badgeLayout: string;
  themeColor: string;
};

export type AemConfigurationThirdpartyGoogleMapsDefaultBoundsSouthWestCorner = {
  lat: string;
  lng: string;
};

export type AemConfigurationThirdpartyGoogleMapsDefaultBoundsNorthEastCorner = {
  lat: string;
  lng: string;
};

export type AemConfigurationThirdpartyGoogleMapsDefaultBounds = {
  southWestCorner: AemConfigurationThirdpartyGoogleMapsDefaultBoundsSouthWestCorner;
  northEastCorner: AemConfigurationThirdpartyGoogleMapsDefaultBoundsNorthEastCorner;
};

export type AemConfigurationThirdpartyGoogleMaps = {
  defaultBounds: AemConfigurationThirdpartyGoogleMapsDefaultBounds;
  defaultZoom: number;
  regionCode: string;
  key: string;
  addressPopup: boolean;
};

export type AemConfigurationThirdpartyPostNl = {
  pId: string;
};

export type AemConfigurationThirdpartyQTip = {
  tipSize: number;
  closeButton: boolean;
};

export type AemConfigurationThirdpartyFacebook = {
  appId: string;
};

export type AemConfigurationThirdpartySocialLogin = {
  enabledList: Array<AemConfigurationThirdpartySocialLoginProvider>;
};

export type AemConfigurationThirdpartyKlarna = {
  clientId: string;
  onSiteMessagingScriptUrl: string;
  onSiteMessagingEnvironment: 'playground' | 'production';
  paymentsScriptUrl: string;
};

export enum AemConfigurationThirdpartySocialLoginProvider {
  FACEBOOK = 'facebook',
}

export type AemConfigurationThirdpartySaferpay = {
  fields: {
    accessToken: string;
    apiUrl: string;
    scriptUrl: string;
  };
  deviceFingerPrinting: {
    dfpBaseUrl: string;
    instanceId: string;
  };
};

export type AemConfigurationThirdpartyApplePay = {
  merchantId: string;
};

export type AemConfigurationThirdpartyZitcha = {
  isEnabled: boolean;
};

export type AemConfigurationThirdparty = {
  zopim: AemConfigurationThirdpartyZopim;
  googleMaps: AemConfigurationThirdpartyGoogleMaps;
  postNl: AemConfigurationThirdpartyPostNl;
  qTip: AemConfigurationThirdpartyQTip;
  facebook: AemConfigurationThirdpartyFacebook;
  socialLogin: AemConfigurationThirdpartySocialLogin;
  klarna: AemConfigurationThirdpartyKlarna;
  saferpay: AemConfigurationThirdpartySaferpay;
  applePay: AemConfigurationThirdpartyApplePay;
  zitcha: AemConfigurationThirdpartyZitcha;
};

export type CdnDomains = {
  activated: boolean;
  domains: string;
  domain: string;
  pathBaseProductDetailLarge: string;
  pathBaseSwatch: string;
  pathBaseTile: string;
  pathBaseNewDesignTile: string;
  pathBaseTileLocation: string;
  pathBaseTileSimple?: string;
};

export type CdnSettings = {
  imageQuality?: string;
  requestPassThrough?: boolean;
};

export type AemConfigurationProductImages = {
  thumbnailBaseUrl: string;
  smallBaseUrl: string;
  largeBaseUrl: string;
  cdnDomains: CdnDomains;
  serviceCdnDomains?: CdnDomains;
};

export type AemConfigurationProductSwatchInformation = {
  maxColors: number;
  maxColorsMobile: number;
  visible: boolean;
  saleVisible: boolean;
  saleLabel: string;
  actionLabel: string;
};

export type ProductSwatchInformation = {
  saleVisible: boolean;
  saleLabel: string;
  actionLabel: string;
};

export type AemConfigurationProductCode = {
  oneSize: string;
};

export type AemConfigurationProductManual = {
  cdnDomain: string;
  location: string;
};

export type AemConfigurationProductVideo = {
  overlayLocation: string;
};

export type AemConfigurationProductPrice = {
  showStartingFromLabel: string;
};

export type AemConfigurationProductVoucher = {
  productCode: string;
  productSize: string;
};

export type AemConfigurationProduct = {
  images: AemConfigurationProductImages;
  swatch: AemConfigurationProductSwatchInformation;
  hideSpaceForRecommendedRetailPrice: boolean;
  code: AemConfigurationProductCode;
  manual: AemConfigurationProductManual;
  video: AemConfigurationProductVideo;
  price: AemConfigurationProductPrice;
  voucher: AemConfigurationProductVoucher;
  sustainabilityCertificationUrl?: string;
};

export type AemConfigurationGiftCardValues = {
  min: string;
  max: string;
  productSize: string;
};

export type AemConfigurationGiftCard = {
  values: AemConfigurationGiftCardValues;
};

export type LatLngLocation = {
  lat: string | number;
  lng: string | number;
};

export enum ABTestContextAwareConfigurationValue {
  ENABLED = 'enabled',
  ENABLED_WITH_AB_TEST = 'enabled-with-ab-test',
  DISABLED = 'disabled',
}

export type AemConfigurationFeatureToggles = {
  account_datalist_element: boolean;
  active_basket_reminder: boolean;
  bpost_address_validation: boolean;
  categorized_product_features: boolean;
  delivery_promise_select_size_label: boolean;
  delivery_promise_standard_delivery_label: boolean;
  ecocheques_cart_item_message: boolean;
  enable_pdp_andes: ABTestContextAwareConfigurationValue;
  enable_pdp_carousel: ABTestContextAwareConfigurationValue;
  lister_colour_swatches_visible_desktop: boolean;
  lister_colour_swatches_visible_mobile: boolean;
  lister_in_store_filter: boolean;
  lister_json_ld: boolean;
  lowest_price_all: boolean;
  no_basket_popup_on_click: boolean;
  our_planet: boolean;
  product_compare: boolean;
  product_gender_icon: boolean;
  product_new_label: boolean;
  product_online_exclusive: boolean;
  show_legals: boolean;
  show_sticky_bar_pdp: boolean;
  stores_own_fascia_first: boolean;
  use_new_auth_flow: boolean;
  visualize_both_delivery_promises: ABTestContextAwareConfigurationValue;
};

export type AemConfigurationListerItems = {
  amountMin: number;
  amountMax: number;
};

export type AemConfigurationListerListerSortProperty = {
  key: string;
  seq: number;
  value: string;
};

export type AemConfigurationListerListerSort = {
  properties: Array<AemConfigurationListerListerSortProperty>;
  defaultValue: string;
};

export type AemConfigurationListerFilter = {
  visible: boolean;
};

export type AemConfigurationListerFredHopper = {
  redirect: boolean;
};

export type AemConfigurationLister = {
  items: AemConfigurationListerItems;
  sort: AemConfigurationListerListerSort;
  filter: AemConfigurationListerFilter;
  fredHopper: AemConfigurationListerFredHopper;
};

export type AemConfigurationReview = {
  enable: boolean;
  listerEnable: boolean;
  amount: string;
  feefoIdentifier?: string;
  reevoo: boolean;
  reevooAccountCode: string;
  reevooShowBrand: boolean;
  reevooStarsEnabled: boolean;
};

export type AemConfigurationPhoneNumbers = {
  phone: string;
  mobilephone: string;
};

export type AemConfigurationPlaceholders = {
  phoneNumbers: AemConfigurationPhoneNumbers;
  vat: string;
};

export type AemConfigurationBuy = {
  delivery: AemConfigurationBuyDelivery;
};

export type AemConfigurationBuyDelivery = {
  maxPickupPoints: number;
  maxStores: number;
};

export type AemConfigurationSEO = {
  productListerSEOTextLocation: string;
};

export type AemConfigOAuth = {
  clientId: string;
};

export enum MainWebShop {
  ASADVENTURE = 'asadventure',
  AYACUCHO = 'ayacucho',
  BEVER = 'bever',
  COTSWOLD = 'cotswold',
  JUTTU = 'juttu',
  RUNNERSNEED = 'runnersneed',
  SNOWANDROCK = 'snowandrock',
}

export type AemConfiguration = {
  activeEnvironment: string;
  alternateUrl: AemConfigurationAlternateUrl;
  analyticsPageType: string;
  basketOverviewUrl: string;
  brandImageCdnUrl: string;
  brandPagePathPrefix?: string;
  brandsPopularOverview: boolean;
  businessUnitId: string;
  buy: AemConfigurationBuy;
  canonicalUrl: string;
  checkoutCc: boolean;
  checkoutHideInitialDeliveryOptions: boolean;
  checkoutLoyaltyCardPrefix: string;
  countryCodes: string;
  countryFlags: string;
  currency: string;
  defaultCountryCode: Lowercase<DefaultCountryCode>;
  deliveryPromises: AemConfigurationDeliveryPromises;
  DPEPath: string;
  elasticsearchIndex: string;
  enableFullVatForm: boolean;
  feature_toggles: AemConfigurationFeatureToggles;
  fictiveWebShop: string;
  fredhopperGlossaryPath: string;
  giftCard: AemConfigurationGiftCard;
  glossaryArrowPosition: string;
  glossaryBoxPosition: string;
  glossaryPath: string;
  hideInNavigation: boolean;
  imageCdnPrefix: string;
  invoiceHideCompany: boolean;
  isEditor: boolean;
  isExploreMore: boolean;
  isInAuthorMode: boolean;
  isShopLanding: boolean;
  isSingleLanguageSite: boolean;
  isTransactionalWebShop: boolean;
  lang: Lowercase<SupportedLanguages>;
  languageAnalytics: string;
  limitationSearchResults: string;
  lister: AemConfigurationLister;
  locale: string;
  mainWebShop: MainWebShop;
  market: string;
  monetateUrl: string;
  oauth: AemConfigOAuth;
  pageInfo: AemConfigurationPageInfo;
  placeholders: AemConfigurationPlaceholders;
  privacyPolicyLink: string;
  product: AemConfigurationProduct;
  referralLinkParameter: string;
  review: AemConfigurationReview;
  root: string;
  rootWithLanguageSuffix: string;
  rootWithoutFinalSlash: string;
  seo: AemConfigurationSEO;
  services: AemConfigurationServices;
  servicesApi: string;
  servicesApiPrefixAemServices: string;
  servicesFredHopperCompare: string;
  servicesFredHopperSearch: string;
  servicesFredHopperSuggest: string;
  shoplanding: AemConfigurationShoplanding;
  shopNumber: string;
  showStoreDetailsCoordinates: boolean;
  sizebayScript: string;
  sizeGuidePath: string;
  storeCheckDefaultLocation: LatLngLocation;
  thirdparty: AemConfigurationThirdparty;
  translationLang: string;
  unsubscribeUrl: string;
  version: string;
  generalConfigUrl: string;
  pageUrls: Partial<PageUrlsInfo>;
};

export type PageUrlsInfo = {
  basketOverview: string;
  finishRegistration: string;
  privacyPolicy: string;
  wishlist: string;
};

export type ABConfiguration = {
  [key in ABTestCookie]?: boolean | string;
};

export type MainWebShopConfiguration = {
  mainWebshopId: number;
  businessUnitId: number;
  businessUnit: string;
  market: string;
  marketId: number;
  name: string;
  fasciaId: number;
  fascia: string;
  orderReference: string;
  urls: StringStringMap;
  showMultiFasciaOnPdp: boolean;
};

export type Configurations = {
  aemConfig: AemConfiguration;
  mainWebShopConfig: MainWebShopConfiguration;
};

export type ConfigurationsWithUrlState = {
  configs: Configurations;
  urlState: UrlState;
};

export type SiteConfig = AemConfiguration & {
  readonly defaultRequestParams: DefaultRequestParams;
  readonly servicesEndpoint: AemConfiguration['services']['api'];
  readonly pdpTemplateName: AemConfiguration['pageInfo']['pdpTemplateName'];
  readonly placeholderVat: AemConfiguration['placeholders']['vat'];
  readonly buyDelivery: AemConfiguration['buy']['delivery'];
  readonly fredHopperSearchUrl: AemConfiguration['services']['fredHopper']['search'];
  readonly authenticationConfig: AuthenticationConfigObject;
  readonly oneSizeProductCode: AemConfiguration['product']['code']['oneSize'];
  readonly socialEnabledList: AemConfiguration['thirdparty']['socialLogin']['enabledList'];
  readonly applePayMerchantId: AemConfiguration['thirdparty']['applePay']['merchantId'];
  readonly saferpayConfig: AemConfiguration['thirdparty']['saferpay'];
  readonly klarnaConfig: AemConfiguration['thirdparty']['klarna'];
  readonly productImageConfig: AemConfiguration['product']['images'];
  readonly productSwatchConfig: AemConfiguration['product']['swatch'];
};
